/* eslint-disable no-unused-vars */
import {
  Box,
  Card,
  colors,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableContainer,
  Tooltip,
  Button,
  IconButton,
  Menu,
  MenuItem
} from '@material-ui/core';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useCheque } from 'src/hooks/cheque-management/use-cheque';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  ChequeListData,
  ChequeListPayload,
  DrNoData,
  POData
} from 'src/redux/slices/cheque-management';
import { AlternatingColorTableRow, LinkComponent } from 'src/components';
import clsx from 'clsx';
import { Pagination } from '@material-ui/lab';
import useResolution from 'src/hooks/useResolution';
import { TextRow } from 'src/views/product/component/TextRow';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { copyToClipboard, formatCurrency, formatDate } from 'src/utils';
import { LiabilitySwitch } from './ChequeWithLiabilitySwitch';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';
import { ChequeListFilterModal } from './ChequeListColumnFilter';
import { ChequeListSortFilterColumnModal } from './ChequeListSortFilterColumn';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ClearIcon from '@material-ui/icons/Clear';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { chequeColumnMap } from 'src/constants/cheque-column';

const useStyles = makeStyles(() => ({
  root: {},
  imgPreview: {
    width: undefined,
    height: 50
  },
  priceInput: {
    height: 30
  },
  lightGrey: {
    backgroundColor: colors.blueGrey[50]
  },
  table: {
    border: '1px solid #a4abba'
  },
  tableCell: {
    textAlign: 'left',
    borderBottom: '1px solid #a4abba'
  },
  tableCellAction: {
    textAlign: 'center',
    borderBottom: '1px solid #a4abba'
  },
  tableRow: {
    borderBottom: '1px solid #a4abba'
  }
}));

interface Props {
  onHandleAppliedFilter: () => void;
}

const Component: FC<Props> = ({ onHandleAppliedFilter }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const {
    chequeList,
    chequeListMeta,
    chequeListParams,
    chequeListColumn,
    setChequeListParams,
    fetchChequeList,
    updateCheque
  } = useCheque();
  const { isMobile } = useResolution();
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState<number>(1);
  const [isOpenColumnFilter, setIsOpenColumnFilter] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = () => setOpenDialog(true);

  const nonSortableCodes = ['Cheque No', 'DR No', 'Supplier'];

  const chequeListHeader = useMemo(() => {
    return chequeListColumn?.filter((col) => col?.isVisible);
  }, [chequeListColumn]);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    column: string
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const handleSortSelection = (direction: 'asc' | 'desc' | '') => {
    if (activeColumn) {
      const mappedColumn = chequeColumnMap[activeColumn] || activeColumn;

      onClickColumn({
        column: mappedColumn === 'terms' ? 'terms' : mappedColumn,
        direction
      });
    }
    handleCloseMenu();
  };

  const onClickColumn = (payload: { column: string; direction: string }) => {
    const { column, direction } = payload;

    // eslint-disable-next-line no-undef
    const newQueryParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      newQueryParams[key] = value;
    });

    if (direction) {
      newQueryParams.sort_by_column = `${column}_${direction}`;
    } else {
      delete newQueryParams.sort_by_column;
    }

    setChequeListParams({
      ...chequeListParams,
      ...newQueryParams,
      page: 1
    });

    onHandleAppliedFilter();

    fetchChequeList({
      ...chequeListParams,
      ...(direction ? { sort_by_column: `${column}_${direction}` } : {}),
      ...(!direction ? { sort_by_column: undefined } : {}),
      page: 1
    });
  };

  const onHandleChangePage = (page: number, params: ChequeListPayload) => {
    setChequeListParams({ ...params, page });
    fetchChequeList({ ...params, page });
    setPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onToggleLiability = useCallback(
    (cheque: ChequeListData) => {
      // Toggle the liability value

      updateCheque(
        {
          cheque_id: cheque?.cheque_id,
          liability: cheque?.liability ? 0 : 1 //if true make it false and vice versa
        },
        false,
        () => fetchChequeList(chequeListParams) //after update call this
      );
    },
    [chequeListParams, fetchChequeList, updateCheque]
  );

  const onHandleCopyProductName = (productName?: string) => {
    copyToClipboard(productName || '');
  };

  const onHandleColumnFilter = () => {
    setIsOpenColumnFilter(true);
  };

  useEffect(() => {
    setPage(chequeListParams?.page || 1);
  }, [chequeListParams]);

  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'end', mb: 1 }}
        style={{ gap: 5 }}
      >
        {isMobile && (
          <Button
            variant="outlined"
            startIcon={<ImportExportIcon />}
            onClick={handleOpenDialog}
          >
            Sort Columns
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<ViewWeekIcon />}
          onClick={onHandleColumnFilter}
        >
          COLUMNS
        </Button>
      </Box>
      {isMobile ? (
        <Card>
          {chequeList?.length > 0 && chequeList ? (
            <PerfectScrollbar>
              {chequeList?.map((i, ind) => (
                <AlternateRowContainer index={ind} key={i.cheque_id}>
                  <div style={{ padding: '1em' }}>
                    {chequeListHeader
                      ?.filter((header) => header.isVisible) // only visible columns
                      .map((header, index) => {
                        const key = header.value;
                        return (
                          <div key={key || index}>
                            {key === 'cheque_no' && (
                              <TextRow
                                label="Cheque no"
                                value={i?.cheque_no}
                                component={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <FileCopyIcon
                                      style={{
                                        cursor: 'pointer',
                                        marginRight: 5
                                      }}
                                      onClick={() =>
                                        onHandleCopyProductName(i?.cheque_no)
                                      }
                                    />
                                    <LinkComponent
                                      openInNewTab
                                      href={`/app/cheque/${i?.cheque_id}`}
                                      title={i.cheque_no ?? ''}
                                      font="Roboto, Helvetica, Arial, sans-serif "
                                    />
                                  </Box>
                                }
                              />
                            )}

                            {key === 'dr_no' && (
                              <TextRow
                                label="DR no"
                                value={
                                  i?.dr_no
                                    ?.map((data: DrNoData) => data?.dr_no || '')
                                    .filter(Boolean)
                                    .join(' / ') || '--'
                                }
                                noBorderBottom
                              />
                            )}

                            {key === 'po_nos' && (
                              <TextRow
                                label="P.O. no"
                                value={
                                  i?.po_nos
                                    ?.map((data: POData) => data?.po_no || '')
                                    .filter(Boolean)
                                    .join(' / ') || '--'
                                }
                                noBorderBottom
                              />
                            )}

                            {key === 'supplier' && (
                              <TextRow
                                label="Supplier"
                                value={i?.supplier || '--'}
                                noBorderBottom
                              />
                            )}

                            {key === 'terms' && (
                              <TextRow
                                label="Terms"
                                value={i?.terms || '--'}
                                noBorderBottom
                              />
                            )}

                            {key === 'pdc_date' && (
                              <TextRow
                                label="PDC Date"
                                value={i?.pdc_date || '--'}
                                noBorderBottom
                              />
                            )}

                            {key === 'created_date' && (
                              <TextRow
                                label="Encoded Date"
                                value={i?.created_date || '--'}
                                noBorderBottom
                              />
                            )}

                            {key === 'amount' && (
                              <TextRow
                                label="Amount"
                                value={
                                  i?.credit_memo_amount &&
                                  Number(i?.credit_memo_amount) > 0
                                    ? `${formatCurrency(i?.amount) ||
                                        formatCurrency(0)} + ${formatCurrency(
                                        i?.credit_memo_amount
                                      )} CM`
                                    : formatCurrency(i?.amount) ||
                                      formatCurrency(0)
                                }
                                noBorderBottom
                              />
                            )}

                            {key === 'liability' && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <Typography
                                  style={{
                                    display: 'flex',
                                    fontWeight: 'bold',
                                    alignItems: 'center'
                                  }}
                                >
                                  Visible in Liability
                                </Typography>
                                <LiabilitySwitch
                                  initialValue={i?.liability}
                                  value={i?.liability}
                                  onChange={() => onToggleLiability(i)}
                                />
                              </Box>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </AlternateRowContainer>
              ))}
            </PerfectScrollbar>
          ) : null}
        </Card>
      ) : (
        <Card className={clsx(classes.root)} style={{ borderRadius: 0 }}>
          <PerfectScrollbar>
            <TableContainer>
              <Table className={classes.table} stickyHeader>
                <TableHead>
                  <TableRow>
                    {chequeListHeader?.map((header, index) => (
                      <TableCell
                        key={index}
                        className={
                          header?.name === 'Visible in Liability'
                            ? classes.tableCellAction
                            : classes.tableCell
                        }
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {!nonSortableCodes.includes(header.name) ? (
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, header.name)}
                              style={{ cursor: 'pointer' }}
                            >
                              <ImportExportIcon
                                fontSize="inherit"
                                style={{ color: 'black' }}
                              />
                            </IconButton>
                          ) : null}
                          {header?.name}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chequeList?.map((item: ChequeListData) => (
                    <AlternatingColorTableRow key={item.cheque_no}>
                      {chequeListHeader?.map((header) => {
                        const key = header.value;

                        return (
                          <TableCell
                            key={key}
                            className={
                              key === 'liability'
                                ? classes.tableCellAction
                                : classes.tableCell
                            }
                          >
                            {key === 'cheque_no' && (
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Tooltip title="copy">
                                  <FileCopyIcon
                                    style={{
                                      cursor: 'pointer',
                                      marginRight: 10
                                    }}
                                    onClick={() =>
                                      onHandleCopyProductName(item?.cheque_no)
                                    }
                                  />
                                </Tooltip>
                                <LinkComponent
                                  href={`/app/cheque/${item?.cheque_id}`}
                                  title={`${item?.cheque_no}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                />
                              </Box>
                            )}

                            {key === 'dr_no' &&
                              (item?.dr_no?.some(
                                (data: DrNoData) => data?.dr_no
                              )
                                ? item.dr_no
                                    .map((data: DrNoData) => data?.dr_no || '')
                                    .filter(Boolean)
                                    .join(' / ')
                                : '--')}

                            {key === 'po_nos' &&
                              (item?.po_nos?.some((data: POData) => data?.po_no)
                                ? item.po_nos
                                    .map((data: POData) => data?.po_no || '')
                                    .filter(Boolean)
                                    .join(' / ')
                                : '--')}

                            {key === 'supplier' && (item?.supplier || '--')}
                            {key === 'terms' && (item?.terms || '--')}
                            {key === 'pdc_date' &&
                              (formatDate(item?.pdc_date) || '--')}
                            {key === 'created_date' &&
                              (formatDate(item?.created_date) || '--')}

                            {key === 'amount' &&
                              (item?.credit_memo_amount &&
                              Number(item?.credit_memo_amount) > 0
                                ? `${formatCurrency(item?.amount) ||
                                    formatCurrency(0)} + ${formatCurrency(
                                    item?.credit_memo_amount
                                  )} CM`
                                : formatCurrency(item?.amount) ||
                                  formatCurrency(0))}

                            {key === 'liability' && (
                              <LiabilitySwitch
                                initialValue={item?.liability}
                                value={item?.liability}
                                onChange={() => onToggleLiability(item)}
                              />
                            )}
                          </TableCell>
                        );
                      })}
                    </AlternatingColorTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PerfectScrollbar>
        </Card>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'end',
          alignContent: 'center',
          width: '100%',
          mt: 3
        }}
      >
        <Pagination
          count={chequeListMeta?.last_page}
          page={page || chequeListMeta?.current_page}
          onChange={(e: any, p: number) =>
            onHandleChangePage(p, chequeListParams)
          }
        />
      </Box>
      <ChequeListFilterModal
        visible={isOpenColumnFilter}
        onHandleClose={() => setIsOpenColumnFilter(false)}
      />

      {/* Sort Options Menu */}
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
        <MenuItem
          onClick={() => {
            handleSortSelection('asc');
            setOpenDialog(false);
          }}
        >
          <ArrowUpwardIcon fontSize="small" style={{ marginRight: 6 }} />
          Ascending
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSortSelection('desc');
            setOpenDialog(false);
          }}
        >
          <ArrowDownwardIcon fontSize="small" style={{ marginRight: 6 }} />
          Descending
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSortSelection('');
            setOpenDialog(false);
          }}
        >
          <ClearIcon fontSize="small" style={{ marginRight: 6 }} />
          Clear Sort
        </MenuItem>
      </Menu>

      <ChequeListSortFilterColumnModal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        headers={chequeListHeader}
        nonSortable={nonSortableCodes}
        onOpenMenu={handleOpenMenu}
      />
    </Box>
  );
};

export const ChequeListTable = memo(Component);
